import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  ViewChild,
  effect,
  inject,
  untracked,
} from "@angular/core";
import {
  Chart,
  Filler,
  Legend,
  LineElement,
  PointElement,
  RadarController,
  RadialLinearScale,
  Tooltip,
} from "chart.js";

import { TemaService } from "../../shared/tema/tema.service";

Chart.register(RadarController, RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

/** Opacidad del relleno de cada serie (el "26" hexadecimal de antes). */
const ALFA_RELLENO = 0x26 / 255;

/** Una serie del radar: un modelo, con un valor por rubro (nulo = no aplicó).
 *  El color puede ser un token, p. ej. var(--hom-serie-1). */
export interface SerieRadar {
  nombre: string;
  valores: (number | null)[];
  color: string;
}

/**
 * Capacidad por rubro en radar (Chart.js, como tickets/general/resumen-ticket).
 * Siempre va junto a una tabla con los números: el radar no es la única
 * forma de leerlos. Un canvas no se pinta con CSS: los colores se leen de
 * los tokens --hom-* al dibujar, y se vuelve a dibujar al cambiar de tema.
 */
@Component({
  selector: "app-radar-rubros",
  standalone: true,
  template: `<div class="hrr"><canvas #lienzo role="img" [attr.aria-label]="descripcion"></canvas></div>`,
  styles: [
    `
      :host {
        display: block;
      }
      .hrr {
        margin: 0 auto;
        max-width: 420px;
        position: relative;
        width: 100%;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RadarRubrosComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() etiquetas: string[] = [];
  @Input() series: SerieRadar[] = [];
  @Input() descripcion = "Capacidad por rubro";

  @ViewChild("lienzo") private lienzo?: ElementRef<HTMLCanvasElement>;
  private grafica: Chart<"radar"> | null = null;
  private readonly tema = inject(TemaService);
  /** Al imprimir, tema-shell.css devuelve los tokens a tinta: se repinta con ellos. */
  private readonly impresion = typeof window !== "undefined" && window.matchMedia ? window.matchMedia("print") : null;
  private readonly alCambiarMedio = () => this.pinta();
  /** Los colores de serie resueltos en pantalla, para el papel (ver pinta()). */
  private readonly seriesEnPantalla = new Map<string, string>();

  constructor() {
    // TemaService pone data-tema en <html> antes de que corra el efecto.
    effect(() => {
      this.tema.actual();
      untracked(() => this.pinta());
    });
    this.impresion?.addEventListener("change", this.alCambiarMedio);
  }

  ngAfterViewInit(): void {
    this.pinta();
  }

  ngOnChanges(): void {
    if (this.lienzo) {
      this.pinta();
    }
  }

  ngOnDestroy(): void {
    this.impresion?.removeEventListener("change", this.alCambiarMedio);
    this.grafica?.destroy();
    this.grafica = null;
  }

  /** El color que da el tema vigente a `color` (un token var(--hom-*) o un literal). */
  private resuelve(color: string): string {
    const caja = this.lienzo?.nativeElement.parentElement;
    if (!caja) {
      return color;
    }
    const sonda = document.createElement("span");
    sonda.style.display = "none";
    sonda.style.color = color;
    caja.appendChild(sonda);
    const valor = getComputedStyle(sonda).color;
    sonda.remove();
    return valor || color;
  }

  /** El color de una serie. En papel conserva el de pantalla: el reset de
   *  impresión deja varias series en la misma tinta y no se distinguirían. */
  private colorSerie(color: string): string {
    if (this.impresion?.matches && this.seriesEnPantalla.has(color)) {
      return this.seriesEnPantalla.get(color)!;
    }
    const valor = this.resuelve(color);
    if (!this.impresion?.matches) {
      this.seriesEnPantalla.set(color, valor);
    }
    return valor;
  }

  private pinta(): void {
    this.grafica?.destroy();
    this.grafica = null;
    if (!this.lienzo || !this.etiquetas.length) {
      return;
    }
    const tinta = this.resuelve("var(--hom-ink)");
    const rejilla = this.resuelve("var(--hom-rejilla)");
    this.grafica = new Chart(this.lienzo.nativeElement, {
      type: "radar",
      data: {
        // En varias lineas: con los nombres largos ("DevOps / configuracion")
        // el radar se cortaba en los bordes.
        labels: this.etiquetas.map((e) => partir(e, 14)),
        datasets: this.series.map((s) => {
          const color = this.colorSerie(s.color);
          return {
            label: s.nombre,
            data: s.valores,
            borderColor: color,
            backgroundColor: conAlfa(color, ALFA_RELLENO),
            pointBackgroundColor: color,
            borderWidth: 2,
            pointRadius: 3,
            fill: true,
            spanGaps: false,
          };
        }),
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        animation: false,
        layout: { padding: 6 },
        scales: {
          r: {
            min: 0,
            max: 100,
            ticks: { stepSize: 25, backdropColor: "transparent", color: this.resuelve("var(--hom-suave)"), font: { size: 10 } },
            pointLabels: { color: tinta, font: { size: 10, weight: 600 } },
            grid: { color: rejilla },
            angleLines: { color: rejilla },
          },
        },
        plugins: {
          legend: { position: "bottom", labels: { boxWidth: 12, color: tinta } },
          tooltip: {
            callbacks: {
              label: (c) => `${c.dataset.label}: ${c.raw == null ? "no aplica" : Number(c.raw).toFixed(1)}`,
            },
          },
        },
      },
    });
  }
}

/** Un color rgb()/rgba() con la opacidad multiplicada por `alfa`. */
function conAlfa(color: string, alfa: number): string {
  const m = color.match(/rgba?\(([^)]+)\)/);
  if (!m) {
    return color;
  }
  const [r, g, b, a = 1] = m[1].split(/[\s,/]+/).filter(Boolean).map(Number);
  return `rgba(${r}, ${g}, ${b}, ${a * alfa})`;
}

/** Parte una etiqueta en renglones de hasta `ancho` caracteres, por palabras. */
function partir(texto: string, ancho: number): string[] {
  const renglones: string[] = [];
  let actual = "";
  for (const palabra of (texto || "").split(/\s+/)) {
    if (actual && (actual + " " + palabra).length > ancho) {
      renglones.push(actual);
      actual = palabra;
    } else {
      actual = actual ? actual + " " + palabra : palabra;
    }
  }
  if (actual) {
    renglones.push(actual);
  }
  return renglones.length ? renglones : [""];
}
